import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import KPICard from '../../components/reports/KPICard';
import ReportChart from '../../components/reports/ReportChart';
import ReportTable from '../../components/reports/ReportTable';
import BranchSelect from '../../components/reports/BranchSelect';
import { calculateWarningKPIs, getTrendData, calculateAging, getUniqueBranches, filterByBranch, exportToCSV } from '../../utils/reportHelpers';

function MediumRiskReport() {
  const navigate = useNavigate();
  const { warnings, customers } = useData();
  
  const [selectedBranch, setSelectedBranch] = useState([]);
  const branches = getUniqueBranches(customers);
  
  let mediumRiskWarnings = warnings.filter(w => w.riskLevel === 'medium' && w.status !== 'resolved');
  mediumRiskWarnings = filterByBranch(mediumRiskWarnings, customers, selectedBranch);
  const kpis = calculateWarningKPIs(mediumRiskWarnings);
  const trendData = getTrendData(mediumRiskWarnings, 90);

  // Check for escalation trend
  const warningsWithTrend = mediumRiskWarnings.map(w => {
    const history = w.history || [];
    if (history.length >= 2) {
      const latest = history[0].score;
      const previous = history[1].score;
      return { ...w, trend: latest - previous };
    }
    return { ...w, trend: 0 };
  });

  const escalating = warningsWithTrend.filter(w => w.trend > 5).length;

  const tableColumns = [
    { key: 'code', label: 'Mã CB', sortable: true },
    { key: 'customerName', label: 'Khách hàng', sortable: true },
    { key: 'score', label: 'Điểm', sortable: true, type: 'number' },
    { 
      key: 'trend', 
      label: 'Xu hướng',
      render: (_, row) => {
        const w = warningsWithTrend.find(x => x.id === row.id);
        if (!w || w.trend === 0) return <span className="text-muted">-</span>;
        return (
          <span className={`badge bg-${w.trend > 0 ? 'warning' : 'success'}`}>
            {w.trend > 0 ? '▲' : '▼'} {Math.abs(w.trend).toFixed(1)}
          </span>
        );
      }
    },
    { key: 'lastCheckedDate', label: 'Kiểm tra gần nhất', type: 'date' },
    { key: 'assignedTo', label: 'Người theo dõi' },
    { key: 'status', label: 'Trạng thái' }
  ];

  const handleExport = () => {
    const exportData = mediumRiskWarnings.map(w => ({
      'Mã': w.code,
      'Khách hàng': w.customerName,
      'Điểm': w.score,
      'Ngày tạo': w.createdDate,
      'Kiểm tra gần nhất': w.lastCheckedDate,
      'Người xử lý': w.assignedTo || 'Chưa phân công'
    }));
    exportToCSV(exportData, 'bao-cao-rui-ro-trung-binh');
  };

  return (
    <div className="container-fluid">
      <div className="page-header-sticky">
        <div className="row mb-4">
          <div className="col-12">
            <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/reports')}>
              <i className="bi bi-arrow-left me-2"></i>
              Quay lại
            </button>
            <h2>
              <i className="bi bi-exclamation-circle me-2 text-warning"></i>
              Báo cáo các trường hợp cảnh báo sớm rủi ro trung bình
            </h2>
            <p className="text-muted">Theo dõi cases ở grey zone, ngăn chặn leo thang</p>
          </div>
        </div>
      </div>

      {/* Branch Filter */}
      <div className="row mb-4">
        <div className="col-md-4">
          <BranchSelect 
            value={selectedBranch}
            onChange={setSelectedBranch}
            branches={branches}
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-3">
          <KPICard 
            title="Tổng rủi ro TB"
            value={mediumRiskWarnings.length}
            icon="bi-exclamation-circle"
            color="warning"
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Xu hướng tăng"
            value={escalating}
            icon="bi-arrow-up-circle"
            color="danger"
            subtitle="Cần chú ý"
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Điểm TB"
            value={kpis.avgScore}
            icon="bi-speedometer2"
            color="info"
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Đang theo dõi"
            value={kpis.inProgress}
            icon="bi-eye"
            color="success"
          />
        </div>
      </div>

      {/* Charts */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-12">
          <ReportChart 
            type="line"
            title="Xu hướng 90 ngày"
            data={trendData}
            height={300}
          />
        </div>
      </div>

      {/* Watchlist Alert */}
      {escalating > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>{escalating} cases</strong> có xu hướng tăng điểm. Kiến nghị tăng tần suất theo dõi.
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Watchlist ({mediumRiskWarnings.length} bản ghi)</h4>
            <button className="btn btn-warning" onClick={handleExport}>
              <i className="bi bi-download me-2"></i>
              Xuất Excel
            </button>
          </div>
          <ReportTable 
            data={mediumRiskWarnings}
            columns={tableColumns}
            onRowClick={(row) => navigate('/warnings', { state: { selectedWarning: row } })}
            pageSize={15}
          />
        </div>
      </div>
    </div>
  );
}

export default MediumRiskReport;
